import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { C } from '../tokens';
import Icon from '../components/Icon';

function BrandSVG({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z" fill="currentColor" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 23 23">
      <path fill="#f35325" d="M1 1h10v10H1z" /><path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" /><path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

const fieldInput = {
  width: '100%', padding: '13px 16px 13px 44px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff', fontSize: 14,
  outline: 'none', color: '#0f172a', transition: 'border-color .2s, box-shadow .2s',
};
const focusIn = (e) => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.primary}20`; };
const focusOut = (e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; };

export default function RegisterPage() {
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);

  const strength = useMemo(() => {
    let s = 0;
    if (pw.length >= 6) s++;
    if (pw.length >= 10) s++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  }, [pw]);
  const strengthLabel = ['Weak', 'Weak', 'Medium', 'Strong', 'Very Strong'][strength];
  const strengthColor = strength <= 1 ? C.primary : strength === 2 ? C.primary : C.green;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'Public Sans','Inter',sans-serif", background: C.bg }}>
      {/* ═══ Header bar ═══ */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#0f172a' }}>
          <div style={{ color: C.primary }}><BrandSVG size={24} /></div>
          <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.02em' }}>Nieramind<span style={{ color: C.primary }}>.AI</span></span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
          <span style={{ color: '#475569' }}>Already have an account?</span>
          <Link to="/login" style={{ color: C.primary, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Sign In</Link>
        </div>
      </header>

      {/* ═══ Main ═══ */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: 480 }}>
          {/* Heading */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-.03em', marginBottom: 8, color: '#0f172a' }}>Create your account</h1>
            <p style={{ color: '#64748b', fontSize: 15 }}>Start your AI journey with Nieramind today.</p>
          </div>

          {/* Card */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f1f5f9', boxShadow: '0 8px 32px rgba(0,0,0,.04)', padding: 32 }}>
            {/* Social */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 32 }}>
              {[[<GoogleIcon key="g" />, 'Google'], [<MicrosoftIcon key="m" />, 'Microsoft']].map(([icon, label]) => (
                <button key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fafafa', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#334155' }}>
                  {icon}<span>{label}</span>
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ fontSize: 14, color: '#64748b', whiteSpace: 'nowrap' }}>or sign up with email</span>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>

            {/* Form */}
            <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Full Name */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6, paddingLeft: 2 }}>Full Name</label>
                <div style={{ position: 'relative' }}>
                  <Icon name="person" size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="text" placeholder="John Doe" style={fieldInput} onFocus={focusIn} onBlur={focusOut} />
                </div>
              </div>

              {/* Work Email */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6, paddingLeft: 2 }}>Work Email</label>
                <div style={{ position: 'relative' }}>
                  <Icon name="mail" size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="email" placeholder="name@company.com" style={fieldInput} onFocus={focusIn} onBlur={focusOut} />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6, paddingLeft: 2 }}>Company Name</label>
                <div style={{ position: 'relative' }}>
                  <Icon name="corporate_fare" size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="text" placeholder="Nieramind Inc." style={fieldInput} onFocus={focusIn} onBlur={focusOut} />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6, paddingLeft: 2 }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Icon name="lock" size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={pw} onChange={e => setPw(e.target.value)}
                    style={{ ...fieldInput, paddingRight: 44 }} onFocus={focusIn} onBlur={focusOut} />
                  <button type="button" onClick={() => setShowPw(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', display: 'grid', placeItems: 'center' }}>
                    <Icon name={showPw ? 'visibility_off' : 'visibility'} size={20} />
                  </button>
                </div>
                {/* Strength bars */}
                <div style={{ display: 'flex', gap: 4, marginTop: 8, height: 6, paddingLeft: 2 }}>
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} style={{ flex: 1, borderRadius: 99, background: i < strength ? strengthColor : '#e2e8f0', transition: 'background .3s' }} />
                  ))}
                </div>
                {pw.length > 0 && (
                  <div style={{ fontSize: 10, fontWeight: 500, color: strengthColor, marginTop: 4, paddingLeft: 2 }}>
                    Strength: {strengthLabel}
                  </div>
                )}
              </div>

              {/* Submit */}
              <button type="submit" style={{ width: '100%', padding: '16px 0', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: `0 8px 24px ${C.primary}33`, marginTop: 4 }}>
                <span>Create Account</span>
                <Icon name="arrow_forward" size={18} style={{ color: '#fff' }} />
              </button>

              {/* Terms */}
              <p style={{ textAlign: 'center', fontSize: 12, color: '#64748b', lineHeight: 1.6, marginTop: 8 }}>
                By creating an account, you agree to our{' '}
                <a href="#/" style={{ color: C.primary, textDecoration: 'none' }}>Terms of Service</a> and{' '}
                <a href="#/" style={{ color: C.primary, textDecoration: 'none' }}>Privacy Policy</a>.
              </p>
            </form>
          </div>

          {/* Footer link */}
          <p style={{ textAlign: 'center', marginTop: 32, fontSize: 14, color: '#475569' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: C.primary, fontWeight: 700, textDecoration: 'none' }}>Sign In</Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ padding: '24px 0', textAlign: 'center', borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em' }}>
          © 2024 Nieramind.AI. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
